import { useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { Reveal } from "./Reveal";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2, MessageCircle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER;
const EMAILJS = {
  service: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  template: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
};

const LOCATIONS = ["Kalyan", "Dombivli", "Thane", "Mulund"];

const waLink = (f) => {
  const text = [
    `New enquiry via website`,
    `Name: ${f.name}`,
    f.phone && `Phone: ${f.phone}`,
    f.email && `Email: ${f.email}`,
    f.subject && `Subject: ${f.subject}`,
    ``,
    f.message,
  ].filter(Boolean).join("\n");
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    const waTab = window.open("", "_blank");
    const time = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    const results = await Promise.allSettled([
      axios.post(`${API}/enquiries`, form),
      emailjs.send(
        EMAILJS.service,
        EMAILJS.template,
        { from_name: form.name, phone: form.phone || "Not provided", message: form.message, time },
        { publicKey: EMAILJS.publicKey }
      ),
    ]);
    const [saved, emailed] = results.map((r) => r.status === "fulfilled");
    if (!emailed) console.error("EmailJS failed", results[1].reason);
    if (!saved && !emailed) {
      if (waTab) waTab.close();
      toast.error("Something went wrong. Please try again or WhatsApp us directly.");
      setLoading(false);
      return;
    }
    if (waTab) waTab.location.href = waLink(form);
    toast.success("Thank you — your enquiry has been received.");
    setSent(form);
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    setLoading(false);
  };

  const inputCls =
    "w-full bg-transparent border-b border-ivory/25 py-3 text-ivory placeholder:text-ivory/40 focus:border-champagne focus:outline-none transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="bg-emerald-dark grain-overlay">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left — enquiry form */}
        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-champagne text-xs uppercase tracking-[0.3em] mb-6">Enquiries</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-ivory leading-[1.05]">
              Start a conversation.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-ivory/60 max-w-lg leading-relaxed">
              For appointments, referrals or professional collaboration, share a few details and
              Dr. Mishra&rsquo;s practice will respond promptly.
            </p>
          </Reveal>

          {!sent ? (
          <Reveal delay={0.15}>
            <form data-testid="enquiry-form" onSubmit={submit} className="mt-12 space-y-8 max-w-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input
                  data-testid="enquiry-name"
                  className={inputCls}
                  placeholder="Full name *"
                  value={form.name}
                  onChange={update("name")}
                />
                <input
                  data-testid="enquiry-email"
                  type="email"
                  className={inputCls}
                  placeholder="Email address *"
                  value={form.email}
                  onChange={update("email")}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input
                  data-testid="enquiry-phone"
                  className={inputCls}
                  placeholder="Phone"
                  value={form.phone}
                  onChange={update("phone")}
                />
                <input
                  data-testid="enquiry-subject"
                  className={inputCls}
                  placeholder="Subject"
                  value={form.subject}
                  onChange={update("subject")}
                />
              </div>
              <textarea
                data-testid="enquiry-message"
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder="Your message *"
                value={form.message}
                onChange={update("message")}
              />
              <button
                data-testid="enquiry-submit-btn"
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-3 bg-champagne text-emerald px-10 py-4 uppercase tracking-[0.15em] text-sm hover:bg-champagne-light transition-colors duration-300 disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          </Reveal>
          ) : (
            <div
              data-testid="enquiry-confirmation"
              className="mt-12 max-w-xl border border-champagne/40 p-8 md:p-10"
            >
              <CheckCircle2 className="text-champagne" size={32} />
              <h3 className="mt-6 font-display font-light text-2xl md:text-3xl text-ivory">
                Thank you, {sent.name.split(" ")[0]}.
              </h3>
              <p className="mt-4 text-ivory/75 text-sm md:text-base leading-relaxed">
                Your enquiry has been received. Dr. Mishra&rsquo;s practice will contact you shortly
                to discuss the next steps.
              </p>
              <p className="mt-6 text-ivory/55 text-sm leading-relaxed">
                A WhatsApp chat with your enquiry pre-filled has opened in a new tab — simply tap
                send there for the fastest response. If it didn&rsquo;t open:
              </p>
              <div className="mt-5 flex flex-wrap gap-4">
                <a
                  data-testid="enquiry-whatsapp-btn"
                  href={waLink(sent)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-emerald-dark px-6 py-3 text-xs uppercase tracking-[0.15em] hover:brightness-110 transition"
                >
                  <MessageCircle size={15} /> Send on WhatsApp
                </a>
                <button
                  data-testid="enquiry-new-btn"
                  onClick={() => setSent(null)}
                  className="text-ivory/60 text-xs uppercase tracking-[0.15em] border-b border-ivory/30 pb-1 hover:text-champagne transition-colors"
                >
                  Send another enquiry
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right — contact details + locations */}
        <div className="lg:col-span-5 lg:border-l lg:border-ivory/15 lg:pl-16">
          <Reveal>
            <div className="space-y-8">
              <a
                href="mailto:drpriyamishraprosthocare@gmail.com"
                data-testid="contact-email"
                className="flex items-start gap-4 group"
              >
                <Mail className="text-champagne mt-1 shrink-0" size={18} />
                <span>
                  <span className="block text-[11px] uppercase tracking-[0.25em] text-ivory/40">Email</span>
                  <span className="text-ivory group-hover:text-champagne transition-colors duration-300 break-all">
                    drpriyamishraprosthocare@gmail.com
                  </span>
                </span>
              </a>
              <a
                href="tel:+917039813389"
                data-testid="contact-phone"
                className="flex items-start gap-4 group"
              >
                <Phone className="text-champagne mt-1 shrink-0" size={18} />
                <span>
                  <span className="block text-[11px] uppercase tracking-[0.25em] text-ivory/40">Phone</span>
                  <span className="text-ivory group-hover:text-champagne transition-colors duration-300">
                    +91 70398 13389
                  </span>
                </span>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-14">
              <div className="flex items-center gap-4 mb-8">
                <MapPin className="text-champagne shrink-0" size={18} />
                <span className="text-[11px] uppercase tracking-[0.25em] text-ivory/40">
                  Freelance Consultation Areas
                </span>
              </div>
              <ul className="space-y-4">
                {LOCATIONS.map((loc, i) => (
                  <li
                    key={loc}
                    data-testid={`location-${i}`}
                    className="font-display font-light text-3xl md:text-4xl text-ivory/90 border-b border-ivory/10 pb-4"
                  >
                    {loc}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-ivory/50 text-sm leading-relaxed">
                Serving patients and referring dentists across the Mumbai Metropolitan Region.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
